const SWIPE_CLOSE_THRESHOLD = 40;

class SmartAppBanner extends HTMLElement {
  #controller = null;
  #touchStartX = 0;
  #touchStartY = 0;
  #isSwiping = false;

  connectedCallback() {
    if (this.dataset.mobileOnly === 'true' && !this.#isMobileDevice()) {
      this.remove();
      return;
    }

    if (this.#isSafari()) {
      this.remove();
      return;
    }

    this.storageKey = `smart-app-banner-dismissed-${this.dataset.sectionId}`;

    if (sessionStorage.getItem(this.storageKey) === 'true') {
      this.remove();
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.closeButton = this.querySelector('[data-smart-app-banner-close]');
    this.closeButton?.addEventListener('click', this.#handleClose, { signal });

    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: true, signal });
    this.addEventListener('touchend', this.#handleTouchEnd, { signal });
    this.addEventListener('touchcancel', this.#resetSwipe, { signal });

    this.#appendLinkParam();
    this.#reveal();
  }

  #appendLinkParam() {
    const { pageType, handle } = this.dataset;
    if (!handle) return;

    const paramKey = pageType === 'product' ? 'products' : pageType === 'collection' ? 'collections' : null;
    if (!paramKey) return;

    const link = this.querySelector('[data-smart-app-banner-link]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    url.searchParams.set(paramKey, handle);
    link.href = url.toString();
  }

  #isSafari() {
    const userAgent = navigator.userAgent;
    return /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Android/.test(userAgent);
  }

  #isMobileDevice() {
    const userAgent = navigator.userAgent;
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /Android|iPhone|iPad|iPod|Mobi/.test(userAgent) || isIPadOS;
  }

  #reveal() {
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #handleTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#isSwiping = true;
    this.style.transition = 'none';
  };

  #handleTouchMove = (event) => {
    if (!this.#isSwiping) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - this.#touchStartY;
    const deltaX = touch.clientX - this.#touchStartX;

    if (deltaY >= 0 || Math.abs(deltaX) > Math.abs(deltaY)) return;

    this.style.transform = `translateY(${deltaY}px)`;
  };

  #handleTouchEnd = (event) => {
    if (!this.#isSwiping) return;

    const deltaY = event.changedTouches[0].clientY - this.#touchStartY;
    this.#isSwiping = false;
    this.style.transition = '';

    if (-deltaY > SWIPE_CLOSE_THRESHOLD) {
      this.#dismiss();
      return;
    }

    this.style.transform = '';
  };

  #resetSwipe = () => {
    this.#isSwiping = false;
    this.style.transition = '';
    this.style.transform = '';
  };

  #handleClose = () => {
    this.#dismiss();
  };

  #dismiss() {
    sessionStorage.setItem(this.storageKey, 'true');
    this.style.transform = 'translateY(-100%)';

    const removeAfterTransition = () => this.remove();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      removeAfterTransition();
      return;
    }

    this.addEventListener('transitionend', removeAfterTransition, { once: true });
  }
}

if (!customElements.get('smart-app-banner')) {
  customElements.define('smart-app-banner', SmartAppBanner);
}
